import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Package, 
  Boxes, 
  MapPin, 
  Truck, 
  ClipboardList, 
  Factory, 
  AlertTriangle,
  Tag,
  TrendingUp,
  ArrowRight,
  RefreshCw,
  GitBranch,
  Calendar,
  ShieldAlert,
  ShieldCheck,
  Clock,
  CheckSquare,
  Square,
  ExternalLink,
  FlaskConical,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ProductionProgressWidget from '../components/ProductionProgressWidget';
import RestockSessionDialog from '../components/RestockSessionDialog';
import WhatsNextHero from '../components/WhatsNextHero';
import DashboardQuickCreate from '../components/DashboardQuickCreate';
import { MyRDTasksWidget } from '../components/rd/MyRDTasksWidget';

const DashboardPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [revisionsDue, setRevisionsDue] = useState(null);
  const [onsiteUnits, setOnsiteUnits] = useState(null);
  const [recallAffected, setRecallAffected] = useState(null);      // iter32
  const [payableBreakdown, setPayableBreakdown] = useState(null);  // iter32
  // iter40 Phase B
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [openOrders, setOpenOrders] = useState([]);
  const [vendorsNeedingReview, setVendorsNeedingReview] = useState({ count: 0, vendors: [] });
  // iter71 — Pending Approvals widget
  const [pendingApprovals, setPendingApprovals] = useState({ items: [], total: 0 });
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkApproving, setBulkApproving] = useState(false);
  // iter75 — Pending Matches widget
  const [pendingMatches, setPendingMatches] = useState({ items: [], total: 0 });
  const [sessionViewerId, setSessionViewerId] = useState(null);
  // iter128 — R&D widget: active prototypes
  const [rdActive, setRdActive] = useState({ prototypes: 0, pilots: 0, concepts: 0, recent: [] });
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, alertsRes, revRes, onsiteRes, recallRes, payableRes, receiptsRes, openRes, vendorsReviewRes, pendingRes, matchesRes, rdRes] = await Promise.all([
        api().get('/dashboard/stats'),
        api().get('/dashboard/alerts'),
        api().get('/dashboard/bom-revisions-due?window_days=30').catch(() => ({ data: null })),
        api().get('/dashboard/onsite-units?limit=10').catch(() => ({ data: null })),
        api().get('/dashboard/recall-affected-units?limit=12').catch(() => ({ data: null })),
        api().get('/timecards/weekly/payable-breakdown').catch(() => ({ data: null })),
        api().get('/restock-sessions/recent/feed?limit=8').catch(() => ({ data: [] })),
        api().get('/expected-orders?status=pending').catch(() => ({ data: [] })),
        api().get('/dashboard/vendors-needing-review').catch(() => ({ data: { count: 0, vendors: [] } })),
        api().get('/dashboard/pending-approvals?limit=25').catch(() => ({ data: { items: [], total: 0 } })),
        api().get('/dashboard/pending-matches?limit=25').catch(() => ({ data: { items: [], total: 0 } })),
        api().get('/rd/projects').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data);
      setRevisionsDue(revRes.data);
      setOnsiteUnits(onsiteRes.data);
      setRecallAffected(recallRes.data);
      setPayableBreakdown(payableRes.data);
      setRecentReceipts(receiptsRes.data || []);
      setOpenOrders(openRes.data || []);
      setVendorsNeedingReview(vendorsReviewRes.data || { count: 0, vendors: [] });
      setPendingApprovals(pendingRes.data || { items: [], total: 0 });
      setPendingMatches(matchesRes.data || { items: [], total: 0 });
      // iter128 — R&D widget rollup
      const rdProjects = Array.isArray(rdRes?.data) ? rdRes.data : [];
      const sortDesc = (a, b) => (b.created_at || '').localeCompare(a.created_at || '');
      setRdActive({
        prototypes: rdProjects.filter(p => p.status === 'Prototype').length,
        pilots: rdProjects.filter(p => p.status === 'Pilot').length,
        concepts: rdProjects.filter(p => p.status === 'Concept').length,
        recent: rdProjects.filter(p => p.status === 'Prototype').sort(sortDesc).slice(0, 4),
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // iter71 — Pending Approvals widget actions
  const canApprove = user?.role === 'Admin' || user?.role === 'ProductionSupervisor' || user?.role === 'Production Supervisor';
  const quickApprove = async (id) => {
    try {
      await api().post(`/work-orders/${id}/approve`);
      toast.success('Approved');
      fetchDashboardData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const openBulkApprove = () => {
    const initial = {};
    (pendingApprovals.items || []).forEach(w => { initial[w.id] = true; });
    setBulkSelected(initial);
    setBulkApproveOpen(true);
  };
  const submitBulkApprove = async () => {
    const ids = Object.entries(bulkSelected).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.error('Select at least one work order'); return; }
    setBulkApproving(true);
    try {
      const r = await api().post('/work-orders/bulk-approve', { ids });
      toast.success(`Approved ${r.data.approved} of ${r.data.requested}`);
      setBulkApproveOpen(false);
      fetchDashboardData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk approve failed'); }
    finally { setBulkApproving(false); }
  };

  const metricCards = [
    { label: 'Total Items', value: stats?.total_items || 0, icon: Boxes, color: 'text-blue-600', path: '/items' },
    { label: 'Vendors', value: stats?.total_vendors || 0, icon: Truck, color: 'text-purple-600', path: '/vendors' },
    { label: 'Locations', value: stats?.total_locations || 0, icon: MapPin, color: 'text-green-600', path: '/locations' },
    { label: 'BOMs', value: stats?.total_boms || 0, icon: ClipboardList, color: 'text-indigo-600', path: '/boms' },
    { label: 'Active Work Orders', value: stats?.active_work_orders || 0, icon: Factory, color: 'text-orange-600', path: '/work-orders' },
    { label: 'Finished Units', value: stats?.finished_units || 0, icon: Tag, color: 'text-teal-600', path: '/finished-units' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slideIn" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm">Overview of your inventory and production system</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchDashboardData}
          className="rounded-sm"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* iter89 — What's Next hero: top-of-dashboard build target */}
      <WhatsNextHero />

      {/* iter92 — Quick-create buttons for all 5 customer-facing doc types */}
      <DashboardQuickCreate />

      <MyRDTasksWidget />

      {/* iter128 — R&D widget: Active prototypes */}
      {(rdActive.prototypes + rdActive.pilots + rdActive.concepts) > 0 && (
        <Card className="industrial-card border-fuchsia-200 bg-fuchsia-50/30" data-testid="rd-active-widget">
          <CardHeader className="border-b border-fuchsia-100 pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-fuchsia-600" />
                Active R&D
                <span className="text-sm font-mono bg-fuchsia-100 text-fuchsia-700 px-2 py-0.5 rounded">
                  {rdActive.prototypes} proto · {rdActive.pilots} pilot · {rdActive.concepts} concept
                </span>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">Projects currently in Prototype — tap any to open its workspace.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate('/rd?status=Prototype')} data-testid="rd-widget-open-all-btn">
              See all <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {rdActive.recent.length === 0 ? (
              <p className="px-4 py-3 text-xs text-slate-500">No prototypes right now. Concepts and pilots are still in the pipeline.</p>
            ) : (
              <div className="divide-y divide-fuchsia-100">
                {rdActive.recent.map(p => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/rd/${p.id}`)}
                    className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-fuchsia-100/40 text-left"
                    data-testid={`rd-widget-row-${p.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FlaskConical className="w-4 h-4 text-fuchsia-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-[11px] text-slate-500">
                          Owner: {p.owner_name || p.created_by_name || '—'}
                          {p.target_launch_date && <span className="text-slate-400"> · target {p.target_launch_date}</span>}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alert Banner */}
      {(stats?.low_stock_items > 0 || alerts.length > 0) && (
        <div className="bg-orange-50 border border-orange-200 rounded-sm p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-orange-800">Attention Required</h3>
            <p className="text-orange-700 text-sm">
              {stats?.low_stock_items > 0 && `${stats.low_stock_items} item(s) below reorder point. `}
              {alerts.length > 0 && `${alerts.length} alert(s) need your attention.`}
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto border-orange-300 text-orange-700 hover:bg-orange-100 rounded-sm"
            onClick={() => navigate('/items')}
          >
            View Items
          </Button>
        </div>
      )}

      {/* iter40 Phase D — Vendors Needing Risk Review */}
      {vendorsNeedingReview.count > 0 && (
        <Card className="industrial-card border-amber-200 bg-amber-50/30" data-testid="vendors-needing-review-widget">
          <CardHeader className="border-b border-amber-100 pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-600" />
                Vendors Needing Risk Review
                <span className="text-sm font-mono bg-amber-100 text-amber-700 px-2 py-0.5 rounded">{vendorsNeedingReview.count}</span>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">Newly added vendors. Confirm risk indices (Delay / Cost / Geopolitical) before sourcing.</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate('/vendors')} data-testid="vendors-review-open-btn">
              Manage <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-amber-100">
              {vendorsNeedingReview.vendors.slice(0, 8).map(v => (
                <button
                  key={v.id}
                  onClick={() => navigate(`/vendors/view/${v.id}`)}
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-amber-100/40 text-left"
                  data-testid={`vendor-review-row-${v.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Truck className="w-4 h-4 text-amber-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{v.vendor_name}</p>
                      <p className="text-[11px] text-slate-500 mono">
                        D:{v.delay_risk || 1} · C:{v.cost_risk || 1} · G:{v.geopolitical_risk || 1}
                        {v.created_at && <span className="text-slate-400"> · added {new Date(v.created_at).toLocaleDateString()}</span>}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* iter71 — Pending Approvals widget (visible to Admin + Supervisor) */}
      {canApprove && pendingApprovals.total > 0 && (
        <Card className="industrial-card border-amber-300" data-testid="pending-approvals-widget">
          <CardHeader className="pb-2 border-b border-amber-100 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
              Pending Approvals
              <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold mono" data-testid="pending-approvals-count">{pendingApprovals.total}</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Operators are blocked until approved</span>
              <Button size="sm" variant="outline" onClick={openBulkApprove} data-testid="bulk-approve-open-btn" className="border-amber-300 text-amber-800 hover:bg-amber-50">
                <ShieldCheck className="w-4 h-4 mr-1" /> Bulk Approve
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-72 overflow-y-auto">
            {pendingApprovals.items.slice(0, 8).map(wo => (
              <div key={wo.id} className="px-4 py-2 border-b border-slate-100 last:border-b-0 hover:bg-amber-50 flex items-center justify-between" data-testid={`pending-approval-row-${wo.id}`}>
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate('/work-orders')}>
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    <Factory className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="mono">{wo.work_order_number || wo.id.slice(0, 8)}</span>
                    <span className="truncate text-slate-700">{wo.product_type_name || ''}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mono">
                    BOM {wo.bom_label || '—'} · Qty {wo.target_quantity || 0}
                    {wo.created_at && <span className="text-slate-400"> · created {new Date(wo.created_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <Button size="sm" onClick={() => quickApprove(wo.id)} className="bg-emerald-600 hover:bg-emerald-700 ml-2" data-testid={`pending-approval-approve-${wo.id}`}>
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Approve
                </Button>
              </div>
            ))}
            {pendingApprovals.total > 8 && (
              <div className="px-4 py-2 text-[11px] text-slate-500 italic text-center">
                +{pendingApprovals.total - 8} more — use Bulk Approve or go to <button onClick={() => navigate('/work-orders')} className="underline hover:text-amber-700">Work Orders</button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* iter75 — Pending Matches widget (engineer/supervisor): BOMs that require a matched
          production file but have none yet. Pairs with the Pending Approvals widget. */}
      {canApprove && pendingMatches.total > 0 && (
        <Card className="industrial-card border-purple-300" data-testid="pending-matches-widget">
          <CardHeader className="pb-2 border-b border-purple-100 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-purple-600" />
              BOMs Awaiting Engineering Match
              <span className="ml-1 text-[11px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-semibold mono" data-testid="pending-matches-count">{pendingMatches.total}</span>
            </CardTitle>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Production blocked until a linked file is marked Matched</span>
          </CardHeader>
          <CardContent className="p-0 max-h-72 overflow-y-auto">
            {pendingMatches.items.slice(0, 8).map(bom => (
              <div key={bom.id} className="px-4 py-2 border-b border-slate-100 last:border-b-0 hover:bg-purple-50 flex items-center justify-between cursor-pointer" data-testid={`pending-match-row-${bom.id}`} onClick={() => navigate(`/boms/view/${bom.id}`)}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    <GitBranch className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="mono">{bom.bom_variant_code || bom.bom_number || bom.id.slice(0, 8)}</span>
                    {bom.bom_revision && <span className="text-[10px] text-slate-400">Rev {bom.bom_revision}</span>}
                    <span className="truncate text-slate-700">{bom.product_type_name || ''}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mono">
                    {bom.linked_files_count > 0 ? `${bom.linked_files_count} linked file(s) · 0 matched` : 'No linked files yet — add CAD/schematic/weld drawing'}
                    {bom.created_at && <span className="text-slate-400"> · created {new Date(bom.created_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="ml-2 border-purple-300 text-purple-700 hover:bg-purple-50" data-testid={`pending-match-review-${bom.id}`}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Review
                </Button>
              </div>
            ))}
            {pendingMatches.total > 8 && (
              <div className="px-4 py-2 text-[11px] text-slate-500 italic text-center">
                +{pendingMatches.total - 8} more — open <button onClick={() => navigate('/boms')} className="underline hover:text-purple-700">BOMs</button> to review
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* iter40 Phase B — Open Orders + Recent Receipts widgets */}
      {(openOrders.length > 0 || recentReceipts.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="receiving-widgets">
          {/* Open Orders */}
          <Card className="industrial-card border-blue-200">
            <CardHeader className="pb-2 border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-blue-600" /> Open Orders ({openOrders.length})
              </CardTitle>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Ordered, not yet received</span>
            </CardHeader>
            <CardContent className="p-0 max-h-72 overflow-y-auto">
              {openOrders.length === 0 ? (
                <p className="text-xs text-slate-400 italic p-4 text-center">Nothing flagged.</p>
              ) : openOrders.slice(0, 8).map(eo => (
                <div key={eo.id} className="px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-blue-50 cursor-pointer flex items-center justify-between"
                  onClick={() => navigate(`/items/view/${eo.item_id}`)}
                  data-testid={`open-order-${eo.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{eo.item_name}</div>
                    <div className="text-[11px] text-slate-500 mono">
                      {eo.purchase_order_number ? `PO ${eo.purchase_order_number} · ` : ''}
                      qty {eo.expected_quantity} · {eo.vendor_name || '—'}
                    </div>
                  </div>
                  {eo.expected_arrival_date && (
                    <div className="text-[10px] text-slate-500 ml-2 shrink-0">
                      ETA {new Date(eo.expected_arrival_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
          {/* Recent Receipts */}
          <Card className="industrial-card border-emerald-200">
            <CardHeader className="pb-2 border-b border-slate-100 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Truck className="w-4 h-4 text-emerald-600" /> Recent Receipts ({recentReceipts.length})
              </CardTitle>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">Click to view session</span>
            </CardHeader>
            <CardContent className="p-0 max-h-72 overflow-y-auto">
              {recentReceipts.length === 0 ? (
                <p className="text-xs text-slate-400 italic p-4 text-center">No receipts in feed.</p>
              ) : recentReceipts.map(s => {
                const issue = s.was_damaged || s.was_delayed || s.was_challenged;
                return (
                  <div key={s.id} className="px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-emerald-50 cursor-pointer flex items-center justify-between"
                    onClick={() => setSessionViewerId(s.id)}
                    data-testid={`recent-receipt-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        +{s.quantity} {s.item_name}
                        {issue && <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />}
                      </div>
                      <div className="text-[11px] text-slate-500 mono truncate">
                        {s.batch_lot ? `lot ${s.batch_lot} · ` : ''}{s.vendor_name || '—'} · {s.carrier === 'Other' ? (s.carrier_other || 'Other') : (s.carrier || '—')}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 ml-2 shrink-0 text-right">
                      {s.received_at ? new Date(s.received_at).toLocaleDateString() : ''}
                      {s.landed_unit_cost_usd ? <div className="mono">${Number(s.landed_unit_cost_usd).toFixed(2)}</div> : null}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {metricCards.map((metric) => (
          <div 
            key={metric.label} 
            className="metric-card cursor-pointer hover:border-slate-300 transition-colors"
            onClick={() => navigate(metric.path)}
            data-testid={`metric-${metric.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-2">
              <metric.icon className={`w-5 h-5 ${metric.color}`} />
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-2xl font-bold mono">{metric.value}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wide">{metric.label}</p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <Card className="lg:col-span-2 industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Recent Transactions</CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/transactions')}
                className="text-slate-500"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {stats?.recent_transactions?.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {stats.recent_transactions.slice(0, 5).map((tx, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 hover:bg-slate-50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-sm ${
                        tx.transaction_type === 'Receive' ? 'bg-green-100 text-green-700' :
                        tx.transaction_type === 'Consume' ? 'bg-blue-100 text-blue-700' :
                        tx.transaction_type === 'Scrap' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        <TrendingUp className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{tx.transaction_type}</p>
                        <p className="text-xs text-slate-500 mono">{tx.item_sku}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold mono">
                        {tx.transaction_type === 'Consume' || tx.transaction_type === 'Scrap' ? '-' : '+'}
                        {tx.quantity} {tx.uom}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(tx.timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No recent transactions</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts Panel */}
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {alerts.length > 0 ? (
              <div className="divide-y divide-slate-100 max-h-80 overflow-auto">
                {alerts.map((alert, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-50">
                    <div className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-sm mb-2 ${
                      alert.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                      alert.severity === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {alert.type.replace('_', ' ').toUpperCase()}
                    </div>
                    <p className="text-sm text-slate-700">{alert.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No active alerts</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* BOM Engineering Review Queue */}
      {revisionsDue && (
        <Card className="industrial-card" data-testid="bom-revisions-due-widget">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-orange-600" /> BOM Engineering Review Queue
              <span className="ml-auto text-xs text-slate-500 mono" data-testid="bom-revisions-due-totals">
                {revisionsDue.totals.total} BOM(s)
                {revisionsDue.totals.overdue > 0 && <span className="text-red-600 ml-2">· {revisionsDue.totals.overdue} overdue</span>}
                {revisionsDue.totals.due_within_7 > 0 && <span className="text-orange-600 ml-2">· {revisionsDue.totals.due_within_7} due within 7d</span>}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {revisionsDue.items.length === 0 ? (
              <div className="p-6 text-center text-sm text-emerald-700" data-testid="bom-revisions-due-empty">
                <Calendar className="w-8 h-8 mx-auto mb-2" />
                No BOMs due for review in the next 30 days.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {revisionsDue.items.slice(0, 8).map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => navigate(`/boms/view/${r.id}`)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
                    data-testid={`bom-revisions-due-row-${r.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 mono">
                        {r.bom_variant_code} <span className="text-slate-400">rev {r.bom_revision}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.developed_by ? `by ${r.developed_by}` : ''}
                        {r.developed_by && r.next_revision_date ? ' · ' : ''}
                        Due {new Date(r.next_revision_date).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`text-xs mono px-2 py-0.5 rounded-sm border ${r.overdue ? 'bg-red-100 text-red-700 border-red-200' : r.days_remaining <= 7 ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                      {r.overdue ? `${Math.abs(r.days_remaining)}d overdue` : `${r.days_remaining}d`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Production Progress (target tracking) */}
      <ProductionProgressWidget />

      {/* Onsite Finished Units */}
      {onsiteUnits && (
        <Card className="industrial-card" data-testid="onsite-units-widget">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Tag className="w-5 h-5 text-emerald-600" /> Onsite Finished Units
              <span className="ml-auto text-xs text-slate-500 mono" data-testid="onsite-units-totals">
                {onsiteUnits.totals.onsite_total} onsite
                {onsiteUnits.totals.onsite_final > 0 && <span className="text-emerald-700 ml-2">· {onsiteUnits.totals.onsite_final} final</span>}
                {onsiteUnits.totals.onsite_part_assemblies > 0 && <span className="text-slate-600 ml-2">· {onsiteUnits.totals.onsite_part_assemblies} parts</span>}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {onsiteUnits.recent.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="onsite-units-empty">
                <Tag className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No onsite units yet. Build something in the Production Queue.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {onsiteUnits.recent.slice(0, 8).map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => navigate('/finished-units')}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
                    data-testid={`onsite-units-row-${u.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 mono">{u.serial_number}</div>
                      <div className="text-xs text-slate-500">
                        {u.product_type} · {u.unit_kind === 'final_product' ? 'Final Product' : 'Part Assembly'}
                        {u.built_by_name ? ` · by ${u.built_by_name}` : ''}
                      </div>
                    </div>
                    <span className="text-xs mono text-slate-500">
                      {u.build_date ? new Date(u.build_date).toLocaleDateString() : '—'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* iter32 — Units Affected by Active Recall */}
      {recallAffected && recallAffected.total > 0 && (
        <Card className="industrial-card border-red-200" data-testid="recall-affected-widget">
          <CardHeader className="border-b border-red-100 pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-red-600" />
                Units Affected by Active Recall
                <span className="text-sm font-mono bg-red-100 text-red-700 px-2 py-0.5 rounded">{recallAffected.total}</span>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">Finished units consuming a lot flagged by {recallAffected.active_recall_count} active recall(s).</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate('/batch-lot-recall')} data-testid="recall-widget-open-btn">
              Manage Recalls <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {(recallAffected.units || []).slice(0, 12).map(u => (
                <button
                  key={u.id}
                  onClick={() => navigate(`/unit-genealogy?unit=${u.serial_number}`)}
                  className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-red-50 text-left"
                  data-testid={`recall-row-${u.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-2 h-2 rounded-full ${u.lifecycle_state === 'shipped' ? 'bg-orange-500' : 'bg-red-500'}`} />
                    <div className="min-w-0">
                      <p className="font-mono text-sm truncate">{u.serial_number}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {u.unit_kind === 'final_product' ? 'Final Product' : 'Part Assembly'} ·{' '}
                        {(u.active_recalls || []).map(r => r.lot).join(', ')}
                      </p>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${u.lifecycle_state === 'shipped' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
                    {u.lifecycle_state}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* iter32 — Payable vs Non-Payable Hours this week */}
      {payableBreakdown && payableBreakdown.users && payableBreakdown.users.length > 0 && (
        <Card className="industrial-card" data-testid="payable-breakdown-widget">
          <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-600" />
                Payable vs Non-Payable Hours · This Week
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                <span className="font-mono text-emerald-600">{payableBreakdown.totals.payable_hours}h payable</span>
                {' · '}
                <span className="font-mono text-slate-500">{payableBreakdown.totals.non_payable_hours}h non-payable</span>
                {' · '}
                <span className="text-slate-500">{payableBreakdown.totals.users} user(s) this week</span>
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => navigate('/shift-scheduling')} data-testid="payable-widget-open-btn">
              Timecards <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
              {payableBreakdown.users.slice(0, 12).map(u => {
                const total = Math.max(u.total_hours, 0.01);
                const payablePct = Math.round((u.payable_hours / total) * 100);
                return (
                  <div key={u.user_id} className="px-4 py-2.5" data-testid={`payable-row-${u.user_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <div className="flex gap-2 text-xs font-mono">
                        <span className="text-emerald-600">{u.payable_hours}h</span>
                        {u.non_payable_hours > 0 && <span className="text-slate-400">+{u.non_payable_hours}h NP</span>}
                        <span className="text-slate-600">= {u.total_hours}h</span>
                      </div>
                    </div>
                    <div className="flex h-2 rounded overflow-hidden bg-slate-100">
                      <div className="bg-emerald-500" style={{ width: `${payablePct}%` }} />
                      <div className="bg-slate-300" style={{ width: `${100 - payablePct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 rounded-sm hover:border-slate-400"
              onClick={() => navigate('/items')}
              data-testid="quick-action-new-item"
            >
              <Boxes className="w-6 h-6 text-blue-600" />
              <span>Add Item</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 rounded-sm hover:border-slate-400"
              onClick={() => navigate('/work-orders')}
              data-testid="quick-action-new-wo"
            >
              <Factory className="w-6 h-6 text-orange-600" />
              <span>New Work Order</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 rounded-sm hover:border-slate-400"
              onClick={() => navigate('/transactions')}
              data-testid="quick-action-receive"
            >
              <TrendingUp className="w-6 h-6 text-green-600" />
              <span>Receive Inventory</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 rounded-sm hover:border-slate-400"
              onClick={() => navigate('/labels')}
              data-testid="quick-action-print-label"
            >
              <Tag className="w-6 h-6 text-purple-600" />
              <span>Print Labels</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Restock Session Viewer Dialog */}
      <RestockSessionDialog open={!!sessionViewerId} onOpenChange={(o) => !o && setSessionViewerId(null)} sessionId={sessionViewerId} />

      {/* iter71 — Bulk Approve Dialog */}
      <Dialog open={bulkApproveOpen} onOpenChange={setBulkApproveOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-approve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Bulk Approve Work Orders</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto py-2">
            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
              <span>{Object.values(bulkSelected).filter(Boolean).length} of {pendingApprovals.items.length} selected</span>
              <div className="flex gap-2">
                <button className="underline hover:text-slate-700" onClick={() => {
                  const all = {}; pendingApprovals.items.forEach(w => { all[w.id] = true; }); setBulkSelected(all);
                }} data-testid="bulk-approve-select-all">Select all</button>
                <button className="underline hover:text-slate-700" onClick={() => setBulkSelected({})} data-testid="bulk-approve-clear">Clear</button>
              </div>
            </div>
            {pendingApprovals.items.map(wo => (
              <label key={wo.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer border border-transparent hover:border-slate-200" data-testid={`bulk-approve-row-${wo.id}`}>
                <button type="button" onClick={() => setBulkSelected(s => ({ ...s, [wo.id]: !s[wo.id] }))} className="shrink-0">
                  {bulkSelected[wo.id] ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                </button>
                <div className="min-w-0 flex-1 text-sm">
                  <div className="font-medium truncate">{wo.work_order_number || wo.id.slice(0, 8)} · {wo.product_type_name || ''}</div>
                  <div className="text-[11px] text-slate-500 mono">BOM {wo.bom_label || '—'} · Qty {wo.target_quantity || 0}</div>
                </div>
              </label>
            ))}
            {pendingApprovals.items.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No pending work orders.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkApproveOpen(false)}>Cancel</Button>
            <Button onClick={submitBulkApprove} disabled={bulkApproving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-approve-submit-btn">
              <ShieldCheck className="w-4 h-4 mr-1" /> Approve {Object.values(bulkSelected).filter(Boolean).length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
